import React, { useState, useCallback, useMemo, useEffect } from "react";
import { RendererProps } from "./index";
import { QuestionData } from "../../../context/questionnaire/types";
import { CheckboxGrid, CheckboxRow } from "./renderer-styles";

type CheckboxRow = { key: string; label: string };

function getMultiCheckboxRows(question: QuestionData): CheckboxRow[] {
  const choices = question.choices || question.options || [];
  if (choices.length > 0) {
    return choices.map((c) => ({
      key: String(c.value),
      label: String(c.title || c.label || c.value),
    }));
  }
  const boxes = question.checkboxes || [];
  return boxes.map((cb) => ({
    key: String(cb.name),
    label: String(cb.title ?? cb.name),
  }));
}

function initialChecked(
  question: QuestionData,
  answer: RendererProps["answer"]
): Record<string, boolean> {
  if (answer?.value && typeof answer.value === "object" && !Array.isArray(answer.value)) {
    return answer.value as Record<string, boolean>;
  }
  if (answer?.values?.[0] && typeof answer.values[0] === "object") {
    return answer.values[0] as Record<string, boolean>;
  }
  const next: Record<string, boolean> = {};
  for (const cb of question.checkboxes || []) {
    if (cb.default_value !== undefined) {
      next[cb.name] = cb.default_value;
    }
  }
  return next;
}

const MultiCheckboxRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {
  const rows = useMemo(() => getMultiCheckboxRows(question), [question]);

  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    initialChecked(question, answer)
  );

  useEffect(() => {
    if (answer) return;
    const anyChecked = Object.values(checked).some(Boolean);
    if (anyChecked) {
      onAnswer(checked, true, [checked]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = useCallback(
    (key: string) => {
      setChecked((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        const anyChecked = Object.values(next).some(Boolean);
        onAnswer(next, !question.required || anyChecked, [next]);
        return next;
      });
    },
    [onAnswer, question.required]
  );

  return (
    <CheckboxGrid>
      {rows.map((row) => (
        <CheckboxRow key={row.key}>
          <input
            type="checkbox"
            value={row.key}
            checked={!!checked[row.key]}
            onChange={() => handleToggle(row.key)}
          />
          {row.label}
        </CheckboxRow>
      ))}
    </CheckboxGrid>
  );
};

export default MultiCheckboxRenderer;
